import { FUND_STORAGE_KEYS } from "@core/constants";
import {
  OnChangeFn,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useDebounceCallback, useSessionStorage } from "usehooks-ts";

const DEFAULT_SORT = [{ id: "fund_name", desc: false }];

export function useFundsTableState() {
  const [filters, setFilters] = useSessionStorage<
    Record<string, string[] | string>
  >(FUND_STORAGE_KEYS.Filters, {});

  const [pagination, setPagination] = useSessionStorage<PaginationState>(
    FUND_STORAGE_KEYS.Pagination,
    {
      pageIndex: 0,
      pageSize: 20,
    }
  );

  const updateFilters = useCallback<typeof setFilters>(
    (val) => {
      setFilters(val);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [setFilters, setPagination]
  );

  const [search, setSearch] = useSessionStorage<string>(
    FUND_STORAGE_KEYS.Search,
    ""
  );
  const updateSearch = useDebounceCallback((value: string) => {
    setSearch(value);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, 500);

  const [sort, setSort] = useSessionStorage<SortingState>(
    FUND_STORAGE_KEYS.Sort,
    DEFAULT_SORT
  );

  const updateSort: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const newSort =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sort)
          : updaterOrValue;

      // If the sorting is empty, set the default sorting to fund_name.
      // But if the fund_name sorting is explicitly set, don't override it.
      if (newSort.length === 0 && sort[0]?.id !== "fund_name") {
        setSort(DEFAULT_SORT);
      } else {
        setSort(newSort);
      }
    },
    [setSort, sort]
  );

  return useMemo(
    () => ({
      filters,
      updateFilters,
      sort,
      updateSort,
      search,
      updateSearch,
      pagination,
      updatePagination: setPagination,
    }),
    [
      filters,
      updateFilters,
      sort,
      updateSort,
      search,
      updateSearch,
      setPagination,
      pagination,
    ]
  );
}
